import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import api from "@api";
import { useLocation, useNavigate } from "react-router-dom";
import "./FabTournament.css";

function FabTournament({ page, settings, organization }) {
  const [seasons, setSeasons] = useState([]);
  const [divisions, setDivisions] = useState({});
  const [expandedSeasonId, setExpandedSeasonId] = useState(null);
  const [tournamentBracketPath, setTournamentBracketPath] =
    useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const content = page?.content || {};
  const themeMode =
    settings?.theme_mode === "light" ? "light" : "dark";

  const pageTitle =
    content.page_title || "Tournament Eligibility";

  const sectionTitle =
    content.section_title || "THE DEETS:";

  const heroImageUrl =
    content.hero_image_url || "/tournament.png";

  const introParagraphOne =
    content.intro_paragraph_one ||
    "FAB’s regular season consists of 8 games. If your team plays the entire season with ZERO forfeits, your team will be secured an invitation into our tournament bracket. If your team forfeits during the regular season, your team will be ineligible to participate in our tournament.";

  const introParagraphTwo =
    content.intro_paragraph_two ||
    "At FAB, we do our best to maintain a high-quality league. Forfeits are disruptive to our schedule and unfair to the opposing team. Our league prides itself on hosting serious and committed teams, therefore forfeits are taken with little tolerance.";

  const introParagraphThree =
    content.intro_paragraph_three ||
    "Tournaments are held at all FAB locations, and may be held outside of the registered location/day/time of your regular season. Please be reminded, we do not accept time/day/location requests.";

  const introParagraphFour =
    content.intro_paragraph_four ||
    "To be eligible to play in the tournament, a player must play one regular season game and complete the player registration form. Teams will be placed based on their competitive level according to their record and strength of schedule.";

  const introParagraphFive =
    content.intro_paragraph_five ||
    "The league fee includes playoff entry, therefore there are no refunds if a team cannot make their tournament game.";

  const tournamentListTitle =
    content.tournament_list_title || "Tournament";

  const bottomImageOneUrl =
    content.bottom_image_one_url || "/tournament1.jpg";

  const bottomImageTwoUrl =
    content.bottom_image_two_url || "/tournament2.jpg";

  const bottomImageThreeUrl =
    content.bottom_image_three_url || "/tournament3.jpg";

  const orgName =
    organization?.name || "SportingHip";

  const seoTitle = `Tournament | ${orgName}`;

  const seoDescription =
    introParagraphOne ||
    introParagraphTwo ||
    `View tournament information for ${orgName}.`;

  useEffect(() => {
    const fetchSeasons = async () => {
      if (!organization?.id) return;

      try {
        const res = await api.get(
          `/api/seasonsv2/tournament?organization_id=${organization.id}`
        );

        setSeasons(res.data?.seasons || []);
      } catch (err) {
        console.error("Error fetching seasons", err);
      }
    };

    fetchSeasons();
  }, [organization?.id]);

  useEffect(() => {
    if (!organization?.id) return;

    let alive = true;

    const fetchTournamentBracketPage = async () => {
      try {
        const res = await api.get(
          "/api/organizations/public/system-page",
          {
            params: {
              organizationId: organization.id,
              systemKey: "tournament_bracket",
            },
          }
        );

        if (!alive) return;

        setTournamentBracketPath(
          res.data?.page?.path || ""
        );
      } catch (err) {
        if (!alive) return;

        console.error(
          "Error fetching tournament bracket system page:",
          err
        );

        setTournamentBracketPath("");
      }
    };

    fetchTournamentBracketPage();

    return () => {
      alive = false;
    };
  }, [organization?.id]);

  useEffect(() => {
    if (!seasons.length) return;

    const fetchAllDivisions = async () => {
      try {
        const seasonDivisions = {};

        for (const season of seasons) {
          const res = await api.get(
            `/api/seasonsv2/${season.id}/divisions`
          );

          if (
            Array.isArray(res.data) &&
            res.data.length > 0
          ) {
            seasonDivisions[season.id] = res.data;
          }
        }

        setDivisions(seasonDivisions);
      } catch (err) {
        console.error(
          "Error fetching divisions:",
          err
        );
      }
    };

    fetchAllDivisions();
  }, [seasons]);

  const handleDivisionClick = (
    divisionId,
    seasonId
  ) => {
    if (!tournamentBracketPath) {
      console.error(
        "Published Tournament Bracket system page not found."
      );

      alert(
        "The tournament bracket page is not currently available."
      );

      return;
    }

    const normalizedPath =
      tournamentBracketPath.replace(/^\/+|\/+$/g, "");

    const hasSlugInPath =
      organization?.slug &&
      location.pathname.includes(
        `/${organization.slug}`
      );

    const basePath = hasSlugInPath
      ? `/${organization.slug}`
      : "";

    navigate(
      `${basePath}/${normalizedPath}?seasonId=${seasonId}&divisionId=${divisionId}`
    );
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>

        <meta
          name="description"
          content={seoDescription}
        />

        <meta
          property="og:title"
          content={seoTitle}
        />

        <meta
          property="og:description"
          content={seoDescription}
        />

        <meta
          property="og:site_name"
          content={orgName}
        />

        <link
          rel="canonical"
          href={
            window.location.origin +
            window.location.pathname
          }
        />
      </Helmet>

      <div
        className={`fab-tournament ${themeMode}`}
      >
        <div className="fab_tournament_container">
          <h1>{pageTitle}</h1>
          <h3>{sectionTitle}</h3>

          {heroImageUrl ? (
            <img
              src={heroImageUrl}
              className="tournament_image"
              alt={pageTitle}
            />
          ) : null}

          <div className="tournament_section">
            <div className="tournament_information">
              <p>{introParagraphOne}</p>
              <p>{introParagraphTwo}</p>
              <p>{introParagraphThree}</p>
              <p>{introParagraphFour}</p>
              <p>{introParagraphFive}</p>
            </div>

            <div className="tournament_division_list">
              <h3>{tournamentListTitle}</h3>

              {Object.keys(divisions).length ===
                0 ? null : (
                <ul className="tournament_division">
                  {seasons.map((season) =>
                    divisions[season.id] ? (
                      <li key={season.id}>
                        <div
                          className="tournament_season_name"
                          onClick={() =>
                            setExpandedSeasonId(
                              expandedSeasonId ===
                                season.id
                                ? null
                                : season.id
                            )
                          }
                        >
                          <strong>
                            {season.name}
                          </strong>
                        </div>

                        {expandedSeasonId ===
                          season.id && (
                            <ul className="division-sublist">
                              {divisions[season.id]
                                .slice()
                                .sort((a, b) => {
                                  const numA =
                                    parseInt(
                                      (
                                        a.name.match(
                                          /\d+/
                                        ) || [0]
                                      )[0],
                                      10
                                    );

                                  const numB =
                                    parseInt(
                                      (
                                        b.name.match(
                                          /\d+/
                                        ) || [0]
                                      )[0],
                                      10
                                    );

                                  return numA - numB;
                                })
                                .map((division) => (
                                  <li
                                    key={division.id}
                                    className="division-link"
                                    onClick={() =>
                                      handleDivisionClick(
                                        division.id,
                                        season.id
                                      )
                                    }
                                  >
                                    {division.name}
                                  </li>
                                ))}
                            </ul>
                          )}
                      </li>
                    ) : null
                  )}
                </ul>
              )}
            </div>

            {bottomImageOneUrl ? (
              <img
                className="tournament1_image"
                src={bottomImageOneUrl}
                alt="Tournament"
              />
            ) : null}

            <div className="tournament_image_container">
              {bottomImageTwoUrl ? (
                <img
                  className="tournament2_image"
                  src={bottomImageTwoUrl}
                  alt="Tournament"
                />
              ) : null}

              {bottomImageThreeUrl ? (
                <img
                  className="tournament3_image"
                  src={bottomImageThreeUrl}
                  alt="Tournament"
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FabTournament;